import { ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { routePlansApi, type RoutePlan, type RoutePlanVisit } from '@/api/route-plans';
import { blockedProfessionalPayload } from '@/utils/api-errors';
import { getCareerColorHex, tintWithWhite } from '@/utils/careers';

// The board's drag & drop wiring: persisting drops (move / place / shift-move / lunch),
// recovering from rejected or aborted drops, and the custom drag ghost for pending visits.
// Extracted from route-plan-board.vue so the board only orchestrates.

// The browser's default drag image snapshots the whole list row (panel included), which looks
// broken. Hand it a small calendar-event-looking ghost instead: career-tinted box with the
// patient name and time, matching how the visit will render once dropped.
const GHOST_TINT_RATIO = 0.15;
const GHOST_GRAB_OFFSET = 12;

interface DragAndDropOptions {
  routePlan: () => RoutePlan;
  busy: Ref<boolean>;
  notify: (text: string, color?: string) => void;
  onUpdated: () => void;
}

type BlockedConfirmState = {
  retry: (overrideBlocked: boolean) => Promise<{ warnings: string[] }>;
  successText?: string;
  caregiverName: string;
  patientName: string;
};

// eslint-disable-next-line max-statements
export function useRoutePlanBoardDragAndDrop({ routePlan, busy, notify, onUpdated }: DragAndDropOptions) {
  const { t } = useI18n();

  // A rejected/aborted drop needs a calendar remount: the server state is unchanged, so the
  // refetch alone won't pull the block back from vue-cal's optimistic position.
  const calendarVersion = ref(0);

  function visitTimeLabel(visit: RoutePlanVisit): string {
    if (visit.fixedStartTime) return t('routePlan.board.fixedAt', { time: visit.fixedStartTime });
    if (visit.timeWindowStart && visit.timeWindowEnd) {
      return `${visit.timeWindowStart}–${visit.timeWindowEnd}`;
    }

    return t('routePlan.board.noWindow');
  }

  function notifyPlacement(warnings: string[], successText = t('routePlan.board.visitSaved')) {
    notify(warnings.length ? warnings.join(' · ') : successText,
      warnings.length ? 'warning' : 'success');
  }

  function placementError(error: unknown): string {
    const data = (error as { response?: { data?: { errors?: string[] } } })?.response?.data;

    return data?.errors?.length ? data.errors.join(' · ') : t('routePlan.visitForm.saveError');
  }

  // A drop rejected as `blocked_professional`: the retry (with the override) is parked here
  // until the user answers the confirm dialog. The calendar meanwhile redraws from server
  // state, so the block visually returns until the user insists.
  const blockedConfirm = ref<BlockedConfirmState | null>(null);

  function handleDropError(
    error: unknown, action: (overrideBlocked: boolean) => Promise<{ warnings: string[] }>,
    successText?: string,
  ) {
    const blocked = blockedProfessionalPayload(error);
    if (blocked) {
      blockedConfirm.value = { retry: action, successText, ...blocked };
    } else {
      notify(placementError(error), 'error');
    }
    calendarVersion.value += 1;
  }

  // Shared by all drop flows: persist, then refetch. Actions receive the blocked-override
  // flag; flows that can't hit a blocked pair (unassign, lunch, shift move) just ignore it.
  async function persistDrop(
    action: (overrideBlocked: boolean) => Promise<{ warnings: string[] }>, successText?: string,
  ) {
    if (busy.value) return;
    busy.value = true;
    try {
      const { warnings } = await action(false);
      notifyPlacement(warnings, successText);
    } catch (error) {
      handleDropError(error, action, successText);
    } finally {
      busy.value = false;
      onUpdated();
    }
  }

  function confirmBlockedOverride() {
    const pending = blockedConfirm.value;
    blockedConfirm.value = null;
    if (!pending) return;

    persistDrop(() => pending.retry(true), pending.successText);
  }

  function cancelBlockedOverride() {
    blockedConfirm.value = null;
  }

  // --- unassign by dragging a calendar block onto the pending panel ---------------------

  // The calendar reports which assignment block is mid-drag (vue-cal drags are native, so
  // the panel can act as a drop target); dropping there unplaces the assignment and the
  // visit returns to the pending pool.
  const draggingAssignmentId = ref<number | null>(null);

  function onAssignmentDragStart(assignmentId: number) {
    draggingAssignmentId.value = assignmentId;
  }

  function onAssignmentDragEnd() {
    draggingAssignmentId.value = null;
  }

  function onUnassignDragOver(event: DragEvent) {
    if (draggingAssignmentId.value !== null) event.preventDefault();
  }

  function onUnassignDrop() {
    const assignmentId = draggingAssignmentId.value;
    draggingAssignmentId.value = null;
    if (assignmentId === null) return;

    persistDrop(async () => {
      await routePlansApi.unplaceAssignment(routePlan().id, assignmentId);

      // vue-cal painted the block wherever it was released; the remount on refetch
      // (calendarVersion watcher in the board) redraws it out of the calendar.
      return { warnings: [] };
    }, t('routePlan.board.unassigned'));
  }

  // Drag a placed visit to another caregiver/time — just a move, never a re-optimization.
  function onEventDrop(payload: { assignmentId: number; caregiverId: number; arrivalTime: string }) {
    persistDrop((overrideBlocked) => routePlansApi.moveAssignment(
      routePlan().id, payload.assignmentId,
      {
        caregiverId: payload.caregiverId,
        arrivalTime: `${routePlan().date} ${payload.arrivalTime}`,
        overrideBlocked,
      },
    ));
  }

  // Drag an existing shift to a new time/caregiver — a proposal, persisted on apply.
  function onShiftDrop(payload: { shiftId: number; caregiverId: number; startTime: string }) {
    persistDrop(() => routePlansApi.moveExistingShift(
      routePlan().id, payload.shiftId, payload.caregiverId,
      `${routePlan().date} ${payload.startTime}`,
    ));
  }

  // Drag a pending visit from the left panel onto a slot.
  function onExternalDrop(payload: { visitId: number; caregiverId: number; arrivalTime: string }) {
    persistDrop((overrideBlocked) => routePlansApi.placeAssignment(
      routePlan().id, payload.visitId,
      {
        caregiverId: payload.caregiverId,
        arrivalTime: `${routePlan().date} ${payload.arrivalTime}`,
        overrideBlocked,
      },
    ));
  }

  // vue-cal lost the drop mid-flight: nothing was saved; redraw from server state.
  function onDropAborted() {
    calendarVersion.value += 1;
  }

  // Drag the lunch to a new time — persisted on the caregiver. The company window is
  // advisory: an out-of-window lunch saves and comes back as a warning to notify.
  function onBreakDrop(payload: { caregiverId: number; startTime: string }) {
    const rpc = routePlan().routePlanCaregivers
      .find(c => c.caregiverId === payload.caregiverId);
    if (!rpc) return;

    persistDrop(
      () => routePlansApi.updateCaregiver(routePlan().id, rpc.id, { lunchStart: payload.startTime }),
    );
  }

  function buildDragGhost(visit: RoutePlanVisit): HTMLElement {
    const color = getCareerColorHex(routePlan().career);
    const ghost = document.createElement('div');
    ghost.textContent = `${visit.patientFullName} · ${visitTimeLabel(visit)}`;
    Object.assign(ghost.style, {
      position: 'fixed',
      top: '-1000px',
      left: '0',
      maxWidth: '220px',
      padding: '6px 10px',
      borderRadius: '4px',
      border: `1px solid ${color}`,
      backgroundColor: tintWithWhite(color, GHOST_TINT_RATIO),
      color,
      font: '600 12px Figtree, sans-serif',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      pointerEvents: 'none',
    });
    document.body.appendChild(ghost);

    return ghost;
  }

  function onPendingDragStart(event: DragEvent, visit: RoutePlanVisit) {
    if (!event.dataTransfer) return;
    event.dataTransfer.setData('application/x-route-plan-visit', String(visit.id));
    event.dataTransfer.effectAllowed = 'move';

    const ghost = buildDragGhost(visit);
    event.dataTransfer.setDragImage(ghost, GHOST_GRAB_OFFSET, GHOST_GRAB_OFFSET);
    // The browser rasterizes the drag image synchronously on dragstart; safe to drop right after.
    window.setTimeout(() => ghost.remove(), 0);
  }

  return {
    calendarVersion,
    visitTimeLabel,
    notifyPlacement,
    onPendingDragStart,
    onEventDrop,
    onShiftDrop,
    onExternalDrop,
    onDropAborted,
    onBreakDrop,
    blockedConfirm,
    confirmBlockedOverride,
    cancelBlockedOverride,
    draggingAssignmentId,
    onAssignmentDragStart,
    onAssignmentDragEnd,
    onUnassignDragOver,
    onUnassignDrop,
  };
}
